import { Link } from "react-router-dom";
import { Phone, MessageCircle, Plane, CheckCircle2, ArrowRight } from "lucide-react";
import { MarketingNav } from "./MarketingNav";
import { MarketingFooter } from "./MarketingFooter";
import { Seo } from "./Seo";
import { FaqSection } from "./Faq";
import { Reveal } from "./anim";
import { AEROSPACE, AEROSPACE_FAQ, CONTACT, IMAGES, SINCE, breadcrumb, faqSchema } from "./marketingData";

const WA_TEXT = encodeURIComponent("Hi ITO, I need to move an aero engine / aerospace cargo. Details:");

const ContactButtons = ({ id }) => (
  <div className="flex flex-wrap gap-3">
    <a href={CONTACT.phoneHref} data-testid={`aero-call-btn-${id}`}
      className="inline-flex items-center gap-2 rounded-full bg-[#C84B31] px-6 py-3 text-sm font-semibold text-white hover:bg-[#a83c26] transition-colors">
      <Phone className="w-4 h-4" /> Call {CONTACT.phone}
    </a>
    <a href={`${CONTACT.whatsappHref}?text=${WA_TEXT}`} target="_blank" rel="noopener noreferrer" data-testid={`aero-whatsapp-btn-${id}`}
      className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-[#0B2A17] hover:bg-[#1fb857] transition-colors">
      <MessageCircle className="w-4 h-4" /> WhatsApp us
    </a>
  </div>
);

const Hero = () => (
  <section className="relative min-h-[78vh] flex items-end pb-16 pt-36 overflow-hidden">
    <img src={IMAGES.aeroEngine} alt="Aero engine on an air-suspension trailer in India" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0E1116] via-[#0E1116]/70 to-[#0E1116]/20" />
    <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full">
      <Reveal>
        <p className="text-xs font-semibold tracking-widest uppercase text-[#D4A373] mb-4 inline-flex items-center gap-2">
          <Plane className="w-4 h-4" /> Aerospace logistics · since {SINCE}
        </p>
        <h1 className="font-heading font-black tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.03] max-w-3xl" data-testid="aero-title">{AEROSPACE.title}</h1>
        <p className="mt-5 text-white/80 max-w-2xl leading-relaxed text-base">{AEROSPACE.body}</p>
        <ul className="mt-8 grid sm:grid-cols-2 gap-3 max-w-2xl">
          {AEROSPACE.points.map((p) => (
            <li key={p} className="flex items-center gap-3 text-sm text-white"><CheckCircle2 className="w-5 h-5 text-[#C84B31] shrink-0" /> {p}</li>
          ))}
        </ul>
        <div className="mt-9"><ContactButtons id="hero" /></div>
      </Reveal>
    </div>
  </section>
);

const Capabilities = () => (
  <section className="py-24" data-testid="aero-capabilities">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <p className="text-xs font-semibold tracking-widest uppercase text-[#D4A373] mb-3">How we protect your engine</p>
      <h2 className="font-heading font-bold text-3xl sm:text-4xl max-w-2xl mb-14">Built for parts that can't take a bump.</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden">
        {AEROSPACE.capabilities.map((c, i) => (
          <Reveal key={c.h} delay={(i % 3) * 0.06} className="bg-[#0E1116] p-8 hover:bg-[#151a22] transition-colors">
            <span className="font-heading text-[#C84B31] text-sm font-bold">0{i + 1}</span>
            <h3 className="font-heading font-semibold text-lg mt-3 mb-2">{c.h}</h3>
            <p className="text-sm text-white/70 leading-relaxed">{c.p}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Cargo = () => (
  <section className="py-24 bg-[#151a22]" data-testid="aero-cargo">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
      <Reveal>
        <img src={IMAGES.aeroShop} alt="Aero engine being loaded into a temperature-controlled truck" className="rounded-2xl w-full h-[420px] object-cover" />
      </Reveal>
      <Reveal delay={0.1}>
        <p className="text-xs font-semibold tracking-widest uppercase text-[#D4A373] mb-3">What we move</p>
        <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-8">Engines, modules and aerospace materials</h2>
        <ul className="space-y-4">
          {AEROSPACE.cargo.map((c) => (
            <li key={c} className="flex items-center gap-3 border-b border-white/10 pb-4 text-white/90"><CheckCircle2 className="w-5 h-5 text-[#C84B31] shrink-0" /> {c}</li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-5 mt-8 text-sm font-semibold">
          <Link to="/fleet/air-suspension-trailer" data-testid="aero-link-air-suspension" className="inline-flex items-center gap-1.5 text-[#D4A373] hover:underline">Air-suspension trailers <ArrowRight className="w-4 h-4" /></Link>
          <Link to="/fleet/reefer-vehicles" data-testid="aero-link-reefer" className="inline-flex items-center gap-1.5 text-[#D4A373] hover:underline">Reefer trucks <ArrowRight className="w-4 h-4" /></Link>
          <Link to="/services/air-express-cargo" data-testid="aero-link-air-cargo" className="inline-flex items-center gap-1.5 text-[#D4A373] hover:underline">AOG air cargo <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const Cta = () => (
  <section className="py-20">
    <div className="max-w-5xl mx-auto px-5 sm:px-8">
      <div className="rounded-2xl border border-white/15 p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6" data-testid="aero-cta">
        <div>
          <h2 className="font-heading font-bold text-2xl mb-1">Have an engine to move?</h2>
          <p className="text-white/70 text-sm">Share the engine type, stand dimensions, pickup and delivery points. We'll reply with a plan and quote.</p>
        </div>
        <ContactButtons id="cta" />
      </div>
    </div>
  </section>
);

export default function AerospacePage() {
  return (
    <div className="bg-[#0E1116] text-white font-body antialiased overflow-x-hidden" data-testid="aerospace-page">
      <Seo
        title="Aero Engine Transport in India | Aerospace Logistics for Engine Makers — ITO"
        description="Shock-free aero engine transport in India for engine manufacturers, MROs and aerospace suppliers. Air-suspension trailers, reefers for prepregs and resins, and AOG air cargo, to any pincode."
        path="/aerospace"
        image={IMAGES.aeroEngine}
        imageAlt="Aero engine on an air-suspension trailer in India"
        keywords="aero engine transport india, aircraft engine transport, aerospace logistics india, jet engine transportation, engine stand transport, mro logistics india, aog logistics india, aerospace parts transport, prepreg transport, air suspension trailer aero engine"
        jsonLd={[
          {
            "@context": "https://schema.org", "@type": "Service",
            serviceType: "Aero engine & aerospace logistics",
            provider: { "@type": "Organization", name: "Indian Transport Organisation" },
            areaServed: "IN", description: AEROSPACE.body,
          },
          breadcrumb([{ name: "Home", path: "/" }, { name: "Aerospace", path: "/aerospace" }]),
          faqSchema(AEROSPACE_FAQ),
        ]}
      />
      <MarketingNav heroDark />
      <Hero />
      <Capabilities />
      <Cargo />
      <Cta />
      <FaqSection faqs={AEROSPACE_FAQ} dark title="Aerospace logistics — frequently asked questions" testid="aero-faq" />
      <MarketingFooter />
    </div>
  );
}
